import { usePlanFeaturesQuery } from '@/hooks/queries/use-plan-features-query';
import type { FeatureFlag } from '@fluenceur/types';

/**
 * Gate decision for a feature-flagged page.
 *
 * - `loading`: the plan-features query has not resolved yet — render nothing (or
 *   a skeleton), never redirect.
 * - `granted`: the flag is present and enabled.
 * - `denied`: the query resolved and the flag is genuinely absent.
 */
export type FeatureGateStatus = 'loading' | 'granted' | 'denied';

/**
 * Pure decision function behind {@link useFeatureFlagGate}, extracted so the
 * branch logic is unit-testable without rendering.
 *
 * `hasFeature` short-circuits to `granted`: the flag can only read `true` once
 * the features payload is loaded, so a positive answer is always resolved.
 * Otherwise we only deny once the query has actually settled (`resolved`).
 * While unresolved we stay in `loading` — this is the fix for the deep-link /
 * hard-reload redirect bug, where `hasFeature()` transiently returns `false`
 * before the query (and the auth it depends on) has answered.
 */
export const featureGateStatus = (resolved: boolean, hasFeature: boolean): FeatureGateStatus => {
  if (hasFeature) return 'granted';
  if (!resolved) return 'loading';
  return 'denied';
};

/**
 * Returns the {@link FeatureGateStatus} for a page gated behind `flag`.
 *
 * Resolution is keyed on `isSuccess || isError`, not `isLoading`: the
 * plan-features query is disabled until the user is authenticated, and a
 * disabled TanStack query reports `isLoading === false` while never having run.
 * Keying off `isLoading` would therefore read as "resolved, flag absent" during
 * the auth/fetch window and redirect a legitimate deep-link away. `isSuccess ||
 * isError` only flips once the query has genuinely answered.
 */
export const useFeatureFlagGate = (flag: FeatureFlag): FeatureGateStatus => {
  const { hasFeature, isSuccess, isError } = usePlanFeaturesQuery();
  return featureGateStatus(isSuccess || isError, hasFeature(flag));
};
